import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Unlock, X, Loader2 } from 'lucide-react';
import secureStorage from '../utils/secureStorage';
import { API_BASE_URL } from '../config';

const API = `${API_BASE_URL}/api`;

/**
 * Exercise Lock button.
 *
 * Renders a padlock icon that reflects the exercise's current lock state
 * (open padlock = unlocked / closed padlock = locked). Clicking it opens
 * a two-step modal:
 *   1) Confirm intent ("Are you sure you want to lock / unlock…?")
 *   2) Enter current login password
 * On success, calls `onChanged({ locked })` so the parent can refresh.
 *
 * The button is only visible to Agency Admin and Super Admin. Everyone
 * else sees a static lock badge (icon only) when the exercise is locked
 * so they understand why writes are being rejected.
 *
 * Props:
 *   - exercise: { id, exercise_name, locked, locked_at, locked_by_name }
 *   - user: current user object (must expose `role`)
 *   - onChanged: (newState) => void  – optional callback after successful toggle
 *   - size: 'sm' | 'md'              – visual size preset (default md)
 *   - className: extra tailwind classes for the trigger
 *   - testIdPrefix: string to namespace data-testid values
 */
const ExerciseLockButton = ({
  exercise,
  user,
  onChanged,
  size = 'md',
  className = '',
  testIdPrefix = 'exercise-lock',
}) => {
  const [locked, setLocked] = useState(Boolean(exercise?.locked));
  const [lockMeta, setLockMeta] = useState({
    locked_at: exercise?.locked_at || null,
    locked_by_name: exercise?.locked_by_name || null,
  });
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isPrivileged =
    user && (user.role === 'Super Admin' || user.role === 'Agency Admin');

  const authHeaders = () => {
    const token = secureStorage.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Keep local state in sync when exercise prop changes
  useEffect(() => {
    setLocked(Boolean(exercise?.locked));
    setLockMeta({
      locked_at: exercise?.locked_at || null,
      locked_by_name: exercise?.locked_by_name || null,
    });
  }, [exercise?.locked, exercise?.locked_at, exercise?.locked_by_name]);

  // Poll authoritative state on mount + every 20s so Dashboard & Management stay in sync
  const refreshStatus = useCallback(async () => {
    if (!exercise?.id) return;
    try {
      const res = await axios.get(
        `${API}/exercise-builder/${exercise.id}/lock-status`,
        { headers: authHeaders() }
      );
      setLocked(Boolean(res.data?.locked));
      setLockMeta({
        locked_at: res.data?.locked_at || null,
        locked_by_name: res.data?.locked_by_name || null,
      });
    } catch {
      // Silent – another poll will retry
    }
  }, [exercise?.id]);

  useEffect(() => {
    refreshStatus();
    const t = setInterval(refreshStatus, 20000);
    return () => clearInterval(t);
  }, [refreshStatus]);

  const openFlow = (e) => {
    e?.stopPropagation?.();
    if (!isPrivileged) return;
    setPassword('');
    setShowConfirm(true);
  };

  const confirmYes = () => {
    setShowConfirm(false);
    setShowPassword(true);
  };

  const submit = async () => {
    if (!password || submitting) return;
    setSubmitting(true);
    try {
      const endpoint = locked ? 'unlock' : 'lock';
      const res = await axios.post(
        `${API}/exercise-builder/${exercise.id}/${endpoint}`,
        { password },
        { headers: authHeaders() }
      );
      const nextLocked = Boolean(res.data?.locked);
      setLocked(nextLocked);
      setLockMeta({
        locked_at: res.data?.locked_at || null,
        locked_by_name: res.data?.locked_by_name || null,
      });
      setShowPassword(false);
      setPassword('');
      toast.success(nextLocked ? 'Exercise locked' : 'Exercise unlocked');
      if (onChanged) onChanged({ locked: nextLocked, ...res.data });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Request failed';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const iconSize = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  const btnSize = size === 'sm' ? 'p-1.5' : 'p-2';

  // Non-privileged users just see a static badge when locked
  if (!isPrivileged) {
    if (!locked) return null;
    return (
      <div
        title={`Locked${lockMeta.locked_by_name ? ` by ${lockMeta.locked_by_name}` : ''}`}
        className={`inline-flex items-center gap-1 ${btnSize} rounded-md border border-red-500/40 bg-red-500/10 text-red-400 ${className}`}
        data-testid={`${testIdPrefix}-badge-${exercise?.id}`}
      >
        <Lock className={iconSize} />
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={openFlow}
        title={
          locked
            ? `Locked${lockMeta.locked_by_name ? ` by ${lockMeta.locked_by_name}` : ''} – click to unlock`
            : 'Lock exercise (read-only for all users)'
        }
        aria-label={locked ? 'Unlock exercise' : 'Lock exercise'}
        data-testid={`${testIdPrefix}-btn-${exercise?.id || 'x'}`}
        className={`inline-flex items-center justify-center ${btnSize} rounded-md border transition-colors ${
          locked
            ? 'border-red-500/50 bg-red-500/10 text-red-400 hover:bg-red-500/20'
            : 'border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50 hover:bg-orange-500/10'
        } ${className}`}
      >
        {locked ? <Lock className={iconSize} /> : <Unlock className={iconSize} />}
      </button>

      {/* Step 1 – confirmation */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-[4000] flex items-center justify-center bg-black/70 p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gray-900 border border-orange-500/40 rounded-lg w-full max-w-md p-5">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-orange-500 font-semibold text-lg flex items-center gap-2">
                {locked ? <Unlock className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
                {locked ? 'Unlock exercise?' : 'Lock exercise?'}
              </h3>
              <button
                onClick={() => setShowConfirm(false)}
                className="text-gray-400 hover:text-white"
                data-testid={`${testIdPrefix}-confirm-close`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-gray-300 text-sm mb-4">
              {locked
                ? `Users will regain the ability to update, add, and delete data inside "${exercise?.exercise_name}".`
                : `While locked, "${exercise?.exercise_name}" and every ICS form, MSEL entry, resource, participant, evaluation and AAR inside it become read-only for everyone.`}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-3 py-2 rounded-md bg-gray-800 hover:bg-gray-700 text-gray-100 text-sm"
                data-testid={`${testIdPrefix}-confirm-cancel`}
              >
                Cancel
              </button>
              <button
                onClick={confirmYes}
                className="px-3 py-2 rounded-md bg-orange-500 hover:bg-orange-600 text-black font-semibold text-sm"
                data-testid={`${testIdPrefix}-confirm-yes`}
              >
                Yes, continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 2 – password */}
      {showPassword && (
        <div
          className="fixed inset-0 z-[4000] flex items-center justify-center bg-black/70 p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gray-900 border border-orange-500/40 rounded-lg w-full max-w-md p-5">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-orange-500 font-semibold text-lg flex items-center gap-2">
                {locked ? <Unlock className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
                Confirm with your password
              </h3>
              <button
                onClick={() => {
                  setShowPassword(false);
                  setPassword('');
                }}
                className="text-gray-400 hover:text-white"
                data-testid={`${testIdPrefix}-password-close`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-gray-400 text-xs mb-3">
              Enter the password you use to log in to EXRSIM.
            </p>
            <input
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
              }}
              placeholder="Your login password"
              className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white outline-none focus:border-orange-500"
              data-testid={`${testIdPrefix}-password-input`}
              disabled={submitting}
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setShowPassword(false);
                  setPassword('');
                }}
                className="px-3 py-2 rounded-md bg-gray-800 hover:bg-gray-700 text-gray-100 text-sm"
                disabled={submitting}
                data-testid={`${testIdPrefix}-password-cancel`}
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={submitting || !password}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-black font-semibold text-sm"
                data-testid={`${testIdPrefix}-password-submit`}
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {locked ? 'Unlock' : 'Lock'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExerciseLockButton;
